import { useEffect, useState } from "react";
import { useOperatorUsers } from "./OperatorUsersContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  createOperatorUserAcc,
  updateOperatorUser,
  deleteOperatorUser,
  checkPassword,
} from "./utils";

const OperatorUserAccForm = () => {
  const [open, setOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [passwordSecurity, setPasswordSecurity] = useState(null);
  const [pwdSuggestion, setPwdSuggestion] = useState();
  const [passStrength, setPassStrength] = useState();
  const [strength, setStrength] = useState({ label: "", score: 0 });
  const [errors, setErrors] = useState({});
  const [passwordApiResult, setPasswordApiResult] = useState(null);
  const {
    operatorUser,
    setOperatorUser,
    setReload,
    selectedOperatorUser,
    setSelectedOperatorUser,
    roles,
    setRoles,
    selectedRoleBtn,
    setSelectedRoleBtn,
    agencies,
    setAgencies,
    selectedAgencyBtn,
    setSelectedAgencyBtn,
    filterValues,
  } = useOperatorUsers();

  useEffect(() => {
    setOpen(operatorUser !== null || selectedOperatorUser !== null);
  }, [operatorUser, selectedOperatorUser]);

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setOperatorUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleAgencyChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    const updatedAgency = agencies.find(
      (eachagency) => eachagency.name === value,
    );
    setOperatorUser((prev) => ({
      ...prev,
      [name]: value,
      ["agencyId"]: updatedAgency.id,
    }));
  };

  const handleRoleBtn = (e) => {
    setSelectedRoleBtn(true);
  };

  const updateStrength = (password) => {
    if (!password) {
      setStrength({ label: "", score: 0 });
      return;
    }

    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;

    let label = "Very Weak";
    if (score >= 4) label = "Strong";
    else if (score === 3) label = "Good";
    else if (score === 2) label = "Fair";
    else if (score === 1) label = "Weak";

    setStrength({ label, score });
  };

  const getStrengthColor = () => {
    if (strength.label === "Very Weak" || strength.label === "Weak")
      return "error";
    if (strength.label === "Fair" || strength.label === "Good")
      return "warning";
    if (strength.label === "Strong") return "success";
    return "inherit";
  };

  const resetPasswordState = () => {
    setPassStrength(null);
    setPwdSuggestion(null);
    setStrength({ label: "", score: 0 });
    setPasswordApiResult(null);
  };

  const handlePasswordChange = async (e) => {
    const value = e.target.value;
    setOperatorUser((prev) => ({
      ...prev,
      password: value,
    }));
    setFormErrors((prev) => ({ ...prev, password: undefined }));

    if (!value) {
      resetPasswordState();
      return;
    }

    updateStrength(value);

    if (value.length >= 3) {
      const result = await checkPassword(
        value,
        setPassStrength,
        setPwdSuggestion,
      );
      setPasswordApiResult(result);
    } else {
      setPassStrength(null);
      setPwdSuggestion(null);
      setPasswordApiResult(null);
    }
  };

  const formValidate = (selectedOperatorUser) => {
    let newFormErrors = {};
    if (
      selectedOperatorUser.login === null ||
      selectedOperatorUser.login.trim() === ""
    ) {
      newFormErrors.login = "Please Enter Login Name";
    }

    if (selectedOperatorUser.password) {
      if (passwordApiResult === null) {
        newFormErrors.password = "Password is not validated yet.";
      } else if (!passwordApiResult.valid) {
        newFormErrors.password = passwordApiResult.details
          ? `${passwordApiResult.details}; ${passwordApiResult.message}`
          : passwordApiResult.message ||
            "Password does not meet security requirements";
      }
    }

    if (
      selectedOperatorUser.name === null ||
      selectedOperatorUser.name.trim() === ""
    ) {
      newFormErrors.name = "Please Enter  Name";
    }
    if (
      selectedOperatorUser.email === null ||
      selectedOperatorUser.email.trim() === ""
    ) {
      newFormErrors.email = "Please Enter Email Address";
    }
    if (
      selectedOperatorUser.company === null ||
      selectedOperatorUser.company.trim() === ""
    ) {
      newFormErrors.company = "Please Enter Company Name";
    }
    if (
      selectedOperatorUser.jobTitle === null ||
      selectedOperatorUser.jobTitle.trim() === ""
    ) {
      newFormErrors.jobTitle = "Please Enter Job Title";
    }

    setFormErrors(newFormErrors);
    return Object.keys(newFormErrors).length === 0;
  };
  const updtUserAcc = (selectedOperatorUser) => {
    if (formValidate(selectedOperatorUser)) {
      if (selectedOperatorUser.id === null)
        createOperatorUserAcc(selectedOperatorUser).then(() => {
          setSelectedOperatorUser(null);
          setOperatorUser(null);
          setReload(true);
          setFormErrors({});
          resetPasswordState();
        });
      else
        updateOperatorUser(selectedOperatorUser).then(() => {
          setSelectedOperatorUser(null);
          setOperatorUser(null);
          setReload(true);
          setFormErrors({});
          resetPasswordState();
        });
    }
  };

  const delUserAcc = (selectedOperatorUser) => {
    deleteOperatorUser(selectedOperatorUser).then(() => {
      setSelectedOperatorUser(null);
      setOperatorUser(null);
      setReload(true);
    });
  };

  const onClose = (e, reason) => {
    setSelectedOperatorUser(null);
    setOperatorUser(null);
    // setRoles(null);
    setFormErrors({});
    resetPasswordState();
  };

  const convertDateISOToUTC = (isoDate) => {
    if (isoDate !== null && isoDate !== "") {
      const date = new Date(isoDate);
      const utcFormatted = `${date.getUTCFullYear()}-${String(
        date.getUTCMonth() + 1,
      ).padStart(2, "0")}-${String(date.getUTCDate()).padStart(
        2,
        "0",
      )} ${String(date.getUTCHours()).padStart(2, "0")}:${String(
        date.getUTCMinutes(),
      ).padStart(2, "0")}:${String(date.getUTCSeconds()).padStart(2, "0")}`;
      return utcFormatted;
    }
  };

  var title = "New Operator User Account";
  if (operatorUser) {
    console.log("operatorUser---->" + JSON.stringify(operatorUser));
    if (operatorUser.id) title = "Edit Operator User Account";
  }

  return (
    operatorUser && (
      <Dialog
        maxWidth={false}
        open={open}
        onClose={onClose}
        position="relative"
      >
        <IconButton
          aria-label="close"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
        <Card sx={{ borderRadius: 3, overflow: "auto" }}>
          <CardHeader
            title={<font className="layer-title"> {title} </font>}
            align="center"
            className={"styledtablerow"}
          />
          <CardContent
            sx={{
              paddingTop: 1,
              paddingLeft: 0,
              paddingRight: 0,
            }}
          >
            <Box
              component="form"
              size="small"
              sx={{
                "& .MuiTextField-root": { width: "35ch" },
                paddingTop: 3,
                align: "center",
              }}
            >
              <Grid container spacing={{ xs: 1 }} sx={{ paddingLeft: 20 }}>
                <Grid alignItems={"stretch"}>
                  <Table size="small">
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          disabled
                          size="small"
                          id="operator-user-id"
                          label="ID"
                          value={operatorUser.id}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          required
                          size="small"
                          id="operator-user-login"
                          label="Login"
                          name="login"
                          value={operatorUser.login}
                          onChange={handleChange}
                          error={formErrors.login}
                          helperText={formErrors.login}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <Box sx={{ width: "100%", maxWidth: "35ch" }}>
                          <TextField
                            size="small"
                            id="operator-user-password"
                            label="Password"
                            type="password"
                            value={operatorUser.password}
                            onChange={handlePasswordChange}
                            name="password"
                            error={!!formErrors.password}
                            helperText={formErrors.password}
                            fullWidth
                          />
                          {(passStrength || strength.score > 0) && (
                            <Box sx={{ mt: 1, mb: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={(strength.score / 4) * 100}
                                color={getStrengthColor()}
                                sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
                              />
                              <Typography
                                variant="caption"
                                fontSize={10}
                                sx={{ display: "block" }}
                              >
                                Strength: {strength.label}
                              </Typography>
                            </Box>
                          )}
                          {pwdSuggestion && (
                            <Box
                              sx={{
                                mt: 0.5,
                                p: 1,
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                                borderRadius: 1,
                                maxHeight: "60px",
                                overflow: "auto",
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontSize={9}
                                color="textSecondary"
                                sx={{
                                  display: "block",
                                  lineHeight: 1.2,
                                  wordBreak: "break-word",
                                }}
                              >
                                {pwdSuggestion}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          required
                          size="small"
                          id="operator-user-name"
                          label="Name"
                          value={operatorUser.name}
                          onChange={handleChange}
                          name="name"
                          error={formErrors.name}
                          helperText={formErrors.name}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          fullWidth
                          required
                          size="small"
                          id="operator-user-email"
                          label="Email"
                          value={operatorUser.email}
                          onChange={handleChange}
                          name="email"
                          error={formErrors.email}
                          helperText={formErrors.email}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          required
                          size="small"
                          id="operator-user-company"
                          label="Company"
                          value={operatorUser.company}
                          onChange={handleChange}
                          name="company"
                          error={formErrors.company}
                          helperText={formErrors.company}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          size="small"
                          id="operator-user-phoneNumber"
                          label="Phone Number"
                          value={operatorUser.phoneNumber}
                          onChange={handleChange}
                          name="phoneNumber"
                        />
                      </TableCell>
                    </TableRow>
                  </Table>
                </Grid>
                <Grid xs={6}>
                  <Table size="small">
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          required
                          size="small"
                          id="operator-user-jobTitle"
                          label="Job Title"
                          value={operatorUser.jobTitle}
                          onChange={handleChange}
                          name="jobTitle"
                          error={formErrors.jobTitle}
                          helperText={formErrors.jobTitle}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          size="small"
                          id="operator-user-faxNumber"
                          label="Fax Number"
                          value={operatorUser.faxNUmber}
                          onChange={handleChange}
                          name="faxNumber"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          size="small"
                          id="operator-user-approved"
                          label="Approved"
                          value={operatorUser.approved}
                          onChange={handleChange}
                          name="approved"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          size="small"
                          id="operator-user-updated"
                          label="Updated"
                          value={convertDateISOToUTC(operatorUser.updated)}
                          onChange={handleChange}
                          name="updated"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        {operatorUser.roles &&
                          operatorUser.roles.length > 0 && (
                            <Typography sx={{ paddingLeft: 1 }}>
                              {JSON.stringify(operatorUser.roles)}
                            </Typography>
                          )}
                        <Button
                          variant="outlined"
                          label="SelectRoles"
                          name="userRoles"
                          value={operatorUser.roles}
                          onClick={handleRoleBtn}
                        >
                          {" "}
                          Select Roles{" "}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <TextField
                          size="small"
                          id="operator-user-agencyId"
                          label="Agency ID"
                          value={operatorUser.agencyId}
                          onChange={handleChange}
                          name="agencyId"
                          disabled
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <FormControl size="small" variant="outlined">
                          <InputLabel id="operator-agency-label">
                            {" "}
                            Agency{" "}
                          </InputLabel>
                          <Select
                            sx={{ minWidth: 150 }}
                            labelId="operator-user-Agency"
                            id="agency-selection"
                            value={operatorUser.agencyName}
                            label="Agency"
                            name="agencyName"
                            onChange={handleAgencyChange}
                          >
                            {agencies.map((eachagency) => (
                              <MenuItem
                                value={eachagency.name}
                                key={eachagency.id}
                              >
                                {eachagency.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  </Table>
                </Grid>
                <Grid xs={6}>
                  <Table size="small">
                    <TableRow>
                      <TableCell className={"tablecellnoborder"}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={operatorUser.neverExpires}
                                onChange={(e) => {
                                  setOperatorUser((operatorUser) => {
                                    return {
                                      ...operatorUser,
                                      neverExpires: !operatorUser.neverExpires,
                                    };
                                  });
                                }}
                              />
                            }
                            label="Never expires"
                          />
                        </FormGroup>
                      </TableCell>
                    </TableRow>
                  </Table>
                </Grid>
              </Grid>
            </Box>
            <Table>
              <TableRow>
                <TableCell align="right">
                  <Button
                    name="Save"
                    label="Save"
                    variant="outlined"
                    size="small"
                    onClick={(e) => updtUserAcc(operatorUser)}
                  >
                    Save
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    name="Delete"
                    label="Delete"
                    variant="outlined"
                    size="small"
                    onClick={(e) => delUserAcc(operatorUser)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            </Table>
          </CardContent>
        </Card>
      </Dialog>
    )
  );
};

export default OperatorUserAccForm;
